import { Box, Button, ButtonGroup, Flex, Icon, Image, Tooltip, useToast } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSendListingMessage } from '../../../../../../hooks/listings/useSendListingMessage'
import { useEffect, useState } from 'react'
import {
  selectCustomerFirstName,
  selectCustomerHasSponsor,
  selectCustomerIsLoanOfficer,
  selectCustomerIsRealEstateAgent,
  selectCustomerPhotoUri
} from '../../../../../../store/selectors/customerProfile'
import { MessageInput, MessageTopicKey } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../../../../store/selectors/sponsorship'
import * as Sentry from '@sentry/browser'
import { ConfettiTarget } from '../../../../../shared/Confetti'
import { hasCustomerToken } from '../../../../../../auth'

const MSG = defineMessages({
  cancel: {
    id: 'BuyDownDirectMessage.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'BuyDownDirectMessage.confirm',
    defaultMessage: 'Confirm'
  },
  buyDownDm: {
    id: 'BuyDownDirectMessage.buyDownDm',
    defaultMessage: 'Ask {name} about buydowns'
  },
  buyDownDmGeneral: {
    id: 'BuyDownDirectMessage.buyDownDmGeneral',
    defaultMessage: 'Ask {name} about these listings'
  },
  success: {
    id: 'BuyDownDirectMessage.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'BuyDownDirectMessage.fail',
    defaultMessage: 'Direct message failed to send.'
  },
  impersonation: {
    id: 'BuyDownDirectMessage.impersonation',
    defaultMessage: 'Messaging not available when impersonating client'
  }
})

/**
 * DM animation is based off EstimatedMonthlyPayment
 *
 * Goal is to eventually move this whole thing to the DS
 */
enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

interface BuyDownDirectMessageProps {
  listingId?: string
}

export const BuyDownDirectMessage = ({ listingId }: BuyDownDirectMessageProps) => {
  const intl = useIntl()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const [isSending, setIsSending] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)

  const isCustomerLO = useSelector(selectCustomerIsLoanOfficer)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerSponsored = useSelector(selectCustomerHasSponsor)
  const isImpersonating = hasCustomerToken()
  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)

  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const getTransformationStyles = (): React.CSSProperties => {
    let transformation = 'translateY(0%)'

    switch (dmStatus) {
      case DMStatus.Initial:
        break
      case DMStatus.Confirmation:
        transformation = 'translateY(-37%)'
        break
      case DMStatus.Fail:
        transformation = 'translateY(-73.5%)'
        break
      case DMStatus.Success:
        transformation = 'translateY(-73.5%)'
        break
    }

    return {
      transform: transformation
    }
  }

  useEffect(() => {
    getTransformationStyles()
  }, [isSending, dmStatus])

  const handleSubmit = async () => {
    setIsSending(true)

    const input: MessageInput = {
      topicKey: MessageTopicKey.ListingsDirectMessageToSponsor,
      message: isCustomerREA
        ? 'Can you tell me more about this listing?'
        : 'Tell me more about Buy Downs, is this a good option for me?',
      topic: 'buy downs as a seller concession'
    }

    if (listingId) {
      input.listingId = listingId
    }

    try {
      await sendListingMessage({
        input
      })
      setDmStatus(DMStatus.Success)
    } catch (error: any) {
      setDmStatus(DMStatus.Fail)

      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: Object.values(error.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsSending(false)
      }, 300)
    }
  }

  return (
    <Flex direction='column' align='center' w='100%'>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position={'relative'} h={12} mb={0} overflow={'hidden'} w={'100%'}>
              <Flex
                direction={'column'}
                gap={4}
                style={getTransformationStyles()}
                transition={'.15s ease-in-out transform'}
              >
                <Flex direction='column' gap={4}>
                  <Box transition={'.15s ease-in-out transform'}>
                    {isImpersonating ? (
                      <Tooltip
                        label={intl.formatMessage(MSG.impersonation)}
                        shouldWrapChildren
                        aria-label={intl.formatMessage(MSG.impersonation)}
                        textAlign={'center'}
                      >
                        <Button
                          colorScheme='secondary'
                          width='100%'
                          isDisabled={isImpersonating}
                          variant={'outline'}
                          bg='neutral.50'
                          leftIcon={
                            <Image
                              src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                              h={6}
                              w={6}
                              borderRadius='50%'
                            />
                          }
                        >
                          {isCustomerLO || isCustomerSponsored
                            ? intl.formatMessage(MSG.buyDownDm, {
                                name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                              })
                            : intl.formatMessage(MSG.buyDownDmGeneral, { name: customerFirstName })}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        colorScheme='secondary'
                        width='100%'
                        visibility={dmStatus === DMStatus.Initial ? 'visible' : 'hidden'}
                        isDisabled={isImpersonating}
                        isLoading={isSending}
                        variant={'outline'}
                        bg='neutral.50'
                        onClick={() => setDmStatus(DMStatus.Confirmation)}
                        tracking={{
                          guid: 'fvem4HRNY1anJsVBGqVXjE',
                          descriptor: 'Initial Button Press to send message'
                        }}
                        leftIcon={
                          <Image
                            src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                            h={6}
                            w={6}
                            borderRadius='50%'
                          />
                        }
                      >
                        {isCustomerLO || isCustomerSponsored
                          ? intl.formatMessage(MSG.buyDownDm, {
                              name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                            })
                          : intl.formatMessage(MSG.buyDownDmGeneral, { name: customerFirstName })}
                      </Button>
                    )}
                  </Box>
                  <ButtonGroup
                    width={'100%'}
                    transition={'1s ease-in-out opacity'}
                    opacity={dmStatus === DMStatus.Fail ? 0 : '100%'}
                  >
                    <Box overflow={'hidden'} width={isSending ? 0 : '50%'} transition={'.1s ease-in-out all'}>
                      <Button
                        variant={'outline'}
                        colorScheme='secondary'
                        bg='neutral.50'
                        w={isSending ? 0 : '100%'}
                        transition={'.2s ease-in-out all'}
                        px={6}
                        tracking={{
                          guid: '83wrMBz9TrzyGNxXrsn3NV',
                          ui_context: 'Buy Down Direct Message',
                          descriptor: 'Cancel submission of dm'
                        }}
                        visibility={dmStatus === DMStatus.Confirmation ? 'visible' : 'hidden'}
                        onClick={() => setDmStatus(DMStatus.Initial)}
                        disabled={isSending || dmStatus === DMStatus.Confirmation}
                      >
                        {intl.formatMessage(MSG.cancel)}
                      </Button>
                    </Box>
                    <Button
                      w='100%'
                      onClick={() => handleSubmit()}
                      colorScheme='primary'
                      isLoading={isSending}
                      visibility={dmStatus === DMStatus.Confirmation ? 'visible' : 'hidden'}
                      opacity={'100% !important'}
                      disabled={dmStatus !== DMStatus.Confirmation}
                      tracking={{
                        guid: '8BoCxCEjj1AasmuS9AbH5Z',
                        ui_context: 'Buy Down Direct Message',
                        descriptor: 'Confirm direct message submission'
                      }}
                    >
                      {intl.formatMessage(MSG.confirm)}
                    </Button>
                  </ButtonGroup>
                </Flex>
                <Flex>
                  <Button
                    w='100%'
                    variant='outline'
                    tracking={{
                      guid: 'tDKXTqRR4Sp8W7ZKSJG7hR',
                      ui_context: 'Buy Down Direct Message',
                      descriptor: 'Clicked on failure / success button'
                    }}
                    visibility={dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail ? 'visible' : 'hidden'}
                    disabled={dmStatus === DMStatus.Confirmation || dmStatus === DMStatus.Initial}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                </Flex>
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
    </Flex>
  )
}
